<template>
  <div class="catalogue">
    <div class="padding">
      <div class="catalogue-content">
        <h2 class="catalogue-title">Catalogue</h2>
      <!-- Public / Shared / Mine switch -->
      <div class="view-switch">
        <button :class="['switch-btn', { active: viewMode === 'public' }]" @click.prevent="setViewMode('public')">Public</button>
        <button :class="['switch-btn', { active: viewMode === 'shared' }]" @click.prevent="setViewMode('shared')">Shared</button>
        <button :class="['switch-btn', { active: viewMode === 'mine' }]" @click.prevent="setViewMode('mine')">Mine</button>
      </div>

      <!-- Filter Panel -->
      <div class="filter-panel">
        <div class="filter-row">
          <div class="filter-group">
            <label>Search</label>
            <input type="text" v-model="filters.search" placeholder="Name or tag..." @input="onFilterChange" />
          </div>

          <div class="filter-group">
            <label>Author</label>
            <input type="text" v-model="filters.owner" placeholder="Username" @input="onFilterChange" />
          </div>

          <div class="filter-group">
            <label>Sort</label>
            <select v-model="filters.sortOrder" @change="onFilterChange">
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
          </div>

          <div class="filter-group filter-toggle-group">
            <label>Info</label>
            <div class="toggle-switch" @click="showInfo = !showInfo">
              <div class="toggle-track" :class="{ 'toggle-on': showInfo }">
                <div class="toggle-thumb"></div>
              </div>
            </div>
          </div>

          <div class="filter-group filter-clear-group">
            <label>&nbsp;</label>
            <button class="clear-btn" @click.prevent="clearFilters">Clear</button>
          </div>
        </div>


      </div>

      <!-- Auth required prompt -->
      <div v-if="needsAuth" class="auth-prompt">
        <div class="auth-prompt-icon">🔒</div>
        <h3 class="auth-prompt-title">Sign in to view this section</h3>
        <p class="auth-prompt-body">
          <strong>{{ viewMode === 'mine' ? 'Mine' : 'Shared' }}</strong> skins are only visible to logged-in users.
          Sign in or create a free account to continue.
        </p>
        <div class="auth-prompt-actions">
          <router-link class="auth-prompt-btn auth-prompt-btn--primary" :to="{ name: 'login', query: { redirect: '/Catalogue' } }">Log in</router-link>
          <router-link class="auth-prompt-btn auth-prompt-btn--ghost" :to="{ name: 'signup' }">Create account</router-link>
        </div>
      </div>

      <!-- Status bar -->
      <div v-if="!needsAuth" class="status-bar">
        <span v-if="loading">Loading...</span>
        <span v-else-if="error" class="status-error">{{ error }}</span>
        <span v-else>Showing {{ pageStart }}–{{ pageEnd }} of {{ totalSkins }} skins</span>
      </div>

      <!-- Skin grid -->
      <!-- Mass Report banner -->
      <div v-if="isMassReportReviewer && !massReportMode" class="mass-report-banner">
        <span>You are viewing as a content reviewer.</span>
        <button class="mass-report-start-btn" @click="startMassReport">Mass Report</button>
      </div>

      <SkinGrid
        v-if="!needsAuth && !loading && skins.length"
        :skins="skins"
        :show-info="showInfo"
        :show-visibility-toggle="viewMode === 'mine' || viewMode === 'shared'"
        :selection-mode="massReportMode"
        :selected-ids="massReportSelectedIds"
        @card-click="openFocus"
        @toggle="toggleMassReportSkin"
        @toggle-public="togglePublic"
      />

      <div v-if="!needsAuth && !loading && !skins.length && !error" class="no-results">
        No skins found matching your criteria.
      </div>

      <!-- Pagination -->
      <div v-if="!needsAuth && !loading && totalPages > 0" class="pagination">
        <div class="pagination-controls">
          <button @click.prevent="goToPage(currentPage - 1)" :disabled="currentPage <= 1">
            &larr; Back
          </button>

          <template v-for="(p, idx) in pageNumbers">
            <span v-if="p === '...'" :key="idx" class="page-ellipsis">…</span>
            <button
              v-else
              :key="idx"
              :class="['page-btn', { 'page-btn-active': p === currentPage }]"
              @click.prevent="goToPage(p)"
            >{{ p }}</button>
          </template>

          <button @click.prevent="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages">
            Forward &rarr;
          </button>
        </div>

        <div class="per-page-selector">
          <label>Per page:</label>
          <select v-model.number="perPage" @change="onPerPageChange">
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="200">200</option>
          </select>
        </div>
      </div>
      </div>
    </div>

    <!-- Mass Report floating controls -->
    <div v-if="massReportMode" class="mass-report-overlay">
      <div class="mass-report-controls">
        <div v-if="massReportDone" class="mass-report-done-msg">
          Report submitted. Thank you.
          <button class="mass-report-cancel-btn" @click="cancelMassReport">Close</button>
        </div>
        <template v-else>
          <span class="mass-report-count">{{ massReportSelectedIds.length }} skin{{ massReportSelectedIds.length === 1 ? '' : 's' }} selected</span>
          <button class="mass-report-cancel-btn" @click="cancelMassReport">Cancel</button>
          <button
            class="mass-report-finish-btn"
            :disabled="massReportSelectedIds.length === 0 || massReportSubmitting"
            @click="finishMassReport"
          >{{ massReportSubmitting ? 'Sending...' : 'Finish Report' }}</button>
        </template>
      </div>
    </div>

    <!-- Focus Modal (thumbnail detail) -->
    <div v-if="focusedSkin" class="focus-modal-backdrop" @click.self="closeFocus">
      <div class="focus-modal">
        <button class="focus-modal-close" @click="closeFocus">✕</button>
        <div class="focus-modal-image-wrap">
          <img :src="focusedSkin.thumbnailUrl" :alt="focusedSkin.name" class="focus-modal-img" />
        </div>
        <div class="focus-modal-info">
          <div class="focus-modal-name">{{ focusedSkin.name }}</div>
          <div class="focus-modal-detail">
            <span>{{ focusedSkin.owner }}</span>
            <span>{{ formatDate(focusedSkin.createdAt) }}</span>
          </div>
          <div class="focus-modal-actions">
            <button v-if="!skinPickerMode" class="assign-btn" @click.stop="openAssignFromFocus">Use</button>
            <button v-if="skinPickerMode" class="pick-skin-btn" @click.stop="pickSkin">Use this skin</button>
            <button v-if="!focusedSkin.isWarhall" class="see3d-btn" @click.stop="openViewerFromFocus">See in 3D</button>
            <button class="report-btn" @click.stop="openReport(focusedSkin)">Report Content</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Report Modal -->
    <div v-if="reportSkin" class="report-modal-backdrop" @click.self="closeReport">
      <div class="report-modal">
        <div class="report-modal-header">
          <span>Report: <strong>{{ reportSkin.name }}</strong></span>
          <button class="report-modal-close" @click="closeReport">✕</button>
        </div>
        <div class="report-modal-body">
          <div v-if="reportDone" class="report-done">
            <p>Thank you. Your report has been submitted.</p>
            <button class="report-cancel-btn" @click="closeReport">Close</button>
          </div>
          <template v-else>
            <p class="report-intro">Select the reason for reporting this content:</p>
            <div class="report-options">
              <label class="report-option">
                <input type="radio" name="reportReason" :value="0" v-model="reportReason" />
                <span class="report-option-label">
                  <span class="report-option-title">Offensive</span>
                  <span class="report-option-desc">Nudity, Sexual, Hate, Discriminatory, Gore</span>
                </span>
              </label>
              <label class="report-option">
                <input type="radio" name="reportReason" :value="1" v-model="reportReason" />
                <span class="report-option-label">
                  <span class="report-option-title">Fraud</span>
                  <span class="report-option-desc">Spam, Low Quality</span>
                </span>
              </label>
              <label class="report-option">
                <input type="radio" name="reportReason" :value="2" v-model="reportReason" />
                <span class="report-option-label">
                  <span class="report-option-title">Intellectual Property Violation</span>
                </span>
              </label>
              <label class="report-option">
                <input type="radio" name="reportReason" :value="3" v-model="reportReason" />
                <span class="report-option-label">
                  <span class="report-option-title">Illegal</span>
                  <span class="report-option-desc">Terrorist, Malware</span>
                </span>
              </label>
            </div>
            <div class="report-modal-footer">
              <button class="report-cancel-btn" @click="closeReport">Cancel</button>
              <button
                class="report-submit-btn"
                :disabled="reportReason === null || reportSubmitting"
                @click="submitReport"
              >{{ reportSubmitting ? 'Sending...' : 'Report Now' }}</button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Viewer Modal -->
    <div v-if="viewerSkin" class="viewer-modal-backdrop" @click.self="closeViewer">
      <div class="viewer-modal">
        <button class="viewer-modal-close" @click="closeViewer">✕</button>
        <div class="viewer-modal-body">
          <ModelViewer
            v-if="viewerSkin.modelUrl"
            :model-url="viewerSkin.modelUrl"
            :view-only="true"
          />
          <div v-else class="viewer-no-model">
            <img :src="viewerSkin.thumbnailUrl" :alt="viewerSkin.name" class="viewer-fallback-img" />
            <p>No 3D model available for this skin.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Modal -->
    <div v-if="assignSkin" class="assign-modal-backdrop" @click.self="closeAssign">
      <div class="assign-modal">
        <div class="assign-modal-header">
          <span>Assign skin: <strong>{{ assignSkin.name }}</strong></span>
          <button class="assign-modal-close" @click="closeAssign">✕</button>
        </div>
        <div class="assign-modal-body">
          <SkinSuccessScreen
            v-if="assignSuccess"
            :image-url="assignSkin ? assignSkin.thumbnailUrl : null"
            title="Done!"
            :subtitle="assignSelection && assignSelection.selectedUnitName && assignSkin ? `${assignSelection.selectedUnitName} will now show with ${assignSkin.name}.` : ''"
            action-label="Done"
            @action="closeAssign"
          />
          <WDSCategorySelector
            v-else
            ref="assignCategorySelector"
            title="Use this as..."
            :image-url="assignSkin ? assignSkin.thumbnailUrl : null"
            :show-unit-name-input="false"
            @selection-changed="onAssignSelectionChanged"
          />
        </div>
        <div v-if="!assignSuccess" class="assign-modal-footer">
          <button class="assign-cancel-btn" @click="closeAssign">Cancel</button>
          <button class="assign-confirm-btn" :disabled="!assignSelection || !assignSelection.isComplete" @click="confirmAssign">Confirm</button>
        </div>

        <!-- Conflict dialog overlay -->
        <div v-if="conflictDialog" class="conflict-overlay">
          <div class="conflict-dialog">
            <p class="conflict-title">There is already a skin associated to <strong>{{ conflictDialog.unitName }}</strong>, do you want to override it, or add it?</p>
            <p class="conflict-hint">Multiple skins on the same unit will result in a random skin picked at creation time.</p>
            <div v-if="conflictDialog.skins && conflictDialog.skins.length" class="conflict-thumbs">
              <img
                v-for="s in conflictDialog.skins"
                :key="s.id"
                :src="s.thumbnailUrl"
                :title="s.name"
                class="conflict-thumb"
              />
            </div>
            <div class="conflict-actions">
              <button class="conflict-btn conflict-btn--cancel" @click="conflictDialog = null">Cancel</button>
              <button class="conflict-btn conflict-btn--override" @click="resolveConflict('override')">Override</button>
              <button class="conflict-btn conflict-btn--add" @click="resolveConflict('add')">Add</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { responseErrorHandling } from "@/assets/js/common.js";
import config from "@/config.js";
import WDSCategorySelector from "@/components/WDSCategorySelector.vue";
import ModelViewer from "@/components/ModelViewer.vue";
import SkinSuccessScreen from "@/components/SkinSuccessScreen.vue";
import SkinGrid from "@/components/SkinGrid.vue";

export default {
  name: "Catalogue",
  components: { WDSCategorySelector, ModelViewer, SkinSuccessScreen, SkinGrid },

  props: {
    skinPickerMode: { type: Boolean, default: false },
  },

  data() {
    return {
      skins: [],
      totalSkins: 0,
      currentPage: 1,
      perPage: 20,
      loading: false,
      error: null,
      showInfo: true,
      viewMode: "public",
      assignSkin: null,
      assignSelection: null,
      assignSuccess: false,
      conflictDialog: null,
      viewerSkin: null,
      viewerReturnSkin: null,
      focusedSkin: null,
      reportSkin: null,
      reportReason: null,
      reportSubmitting: false,
      reportDone: false,
      massReportMode: false,
      massReportSelectedIds: [],
      massReportSubmitting: false,
      massReportDone: false,
      filters: {
        search: "",
        owner: "",
        sortOrder: "newest",
      },
    };
  },

  computed: {
    needsAuth() {
      return (this.viewMode === 'shared' || this.viewMode === 'mine') && !this.$store.state.userid;
    },

    isMassReportReviewer() {
      return this.$route && this.$route.query.review === config.reviewToken;
    },

    totalPages() {
      return Math.ceil(this.totalSkins / this.perPage);
    },

    pageStart() {
      return this.totalSkins === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    },

    pageEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalSkins);
    },

    pageNumbers() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;

      if (total <= 1) return total === 0 ? [] : [1];

      const pages = [];
      pages.push(1);

      const rangeStart = Math.max(2, current - delta);
      const rangeEnd = Math.min(total - 1, current + delta);

      if (rangeStart > 2) pages.push("...");
      for (let i = rangeStart; i <= rangeEnd; i++) {
        pages.push(i);
      }
      if (rangeEnd < total - 1) pages.push("...");

      pages.push(total);
      return pages;
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.fetchSkins();
    });
  },

  created() {
    // Plain (non-reactive) properties for debounce and race-condition guard
    this._searchTimer = null;
    this._fetchSeq = 0;
  },

  mounted() {
    // Also fetch when used as an inline component (not via router navigation)
    if (!this.skins.length) this.fetchSkins();
  },

  methods: {
    addTagFilter(tagName) {
      this.filters.search = tagName;
      this.currentPage = 1;
      this.fetchSkins();
    },

    async fetchSkins() {
      this.loading = true;
      this.error = null;

      // Sequence counter: discard responses from earlier searches
      const seq = ++this._fetchSeq;

      try {
        const params = new URLSearchParams({
          viewMode:  this.viewMode,
          page:      this.currentPage,
          perPage:   this.perPage,
          sortOrder: this.filters.sortOrder,
        });
        if (this.filters.search) params.set('search', this.filters.search);
        if (this.filters.owner)  params.set('owner',  this.filters.owner);

        const res = await fetch(`/GetCatalogueSkins?${params}`, {
          credentials: 'include',
        });
        const data = await res.json();

        // Ignore if a newer request has already been issued
        if (seq !== this._fetchSeq) return;

        if (data.error !== 0) {
          this.error = data.result || 'Failed to load skins.';
          this.skins = [];
          this.totalSkins = 0;
        } else {
          this.skins = data.result.skins;
          this.totalSkins = data.result.total;
        }
      } catch (err) {
        if (seq !== this._fetchSeq) return;
        this.error = 'Could not reach the server.';
        this.skins = [];
        this.totalSkins = 0;
      } finally {
        if (seq === this._fetchSeq) this.loading = false;
      }
    },

    async togglePublic(skin) {
      const newValue = !skin.isPublic;
      try {
        const res = await fetch('/SetSkinPublic', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skinId: skin.id, isPublic: newValue }),
        });
        const data = await res.json();
        if (data.error !== 0) {
          alert('Failed to update visibility: ' + (data.result || 'Unknown error'));
          return;
        }
        skin.isPublic = newValue;
      } catch (err) {
        alert('Could not reach the server.');
      }
    },

    setViewMode(mode) {
      this.viewMode = mode;
      this.currentPage = 1;
      if (!this.needsAuth) this.fetchSkins();
    },

    onFilterChange() {
      this.currentPage = 1;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.fetchSkins(), 300);
    },

    clearFilters() {
      this.filters = {
        search: "",
        owner: "",
        sortOrder: "newest",
      };
      this.currentPage = 1;
      this.fetchSkins();
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchSkins();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    onPerPageChange() {
      this.currentPage = 1;
      this.fetchSkins();
    },

    openFocus(skin) {
      this.focusedSkin = skin;
    },

    closeFocus() {
      this.focusedSkin = null;
    },

    openAssignFromFocus() {
      this.assignSkin = this.focusedSkin;
      this.assignSelection = null;
      this.focusedSkin = null;
    },

    startMassReport() {
      this.massReportMode = true;
      this.massReportSelectedIds = [];
      this.massReportSubmitting = false;
      this.massReportDone = false;
    },

    cancelMassReport() {
      this.massReportMode = false;
      this.massReportSelectedIds = [];
      this.massReportSubmitting = false;
      this.massReportDone = false;
    },

    toggleMassReportSkin(skin) {
      const id = String(skin.id);
      const idx = this.massReportSelectedIds.indexOf(id);
      if (idx === -1) {
        this.massReportSelectedIds.push(id);
      } else {
        this.massReportSelectedIds.splice(idx, 1);
      }
    },

    async finishMassReport() {
      if (this.massReportSelectedIds.length === 0 || this.massReportSubmitting) return;
      this.massReportSubmitting = true;
      try {
        const res = await fetch('/MassReportSkins', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skinIds: this.massReportSelectedIds.map(Number) }),
        });
        const data = await res.json();
        if (data.error !== 0) {
          alert('Failed to submit report: ' + (data.result || 'Unknown error'));
          return;
        }
        this.massReportDone = true;
        this.massReportSelectedIds = [];
      } catch (err) {
        alert('Could not reach the server.');
      } finally {
        this.massReportSubmitting = false;
      }
    },

    openReport(skin) {
      this.reportSkin = skin;
      this.reportReason = null;
      this.reportSubmitting = false;
      this.reportDone = false;
    },

    closeReport() {
      this.reportSkin = null;
      this.reportReason = null;
      this.reportSubmitting = false;
      this.reportDone = false;
    },

    async submitReport() {
      if (this.reportReason === null || this.reportSubmitting) return;
      this.reportSubmitting = true;
      try {
        const res = await fetch('/ReportSkin', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ skinId: this.reportSkin.id, reason: this.reportReason }),
        });
        const data = await res.json();
        if (data.error !== 0) {
          alert('Failed to submit report: ' + (data.result || 'Unknown error'));
          return;
        }
        this.reportDone = true;
      } catch (err) {
        alert('Could not reach the server.');
      } finally {
        this.reportSubmitting = false;
      }
    },

    pickSkin() {
      this.$emit('skin-picked', {
        id: this.focusedSkin.id,
        name: this.focusedSkin.name,
        thumbnailUrl: this.focusedSkin.thumbnailUrl,
      });
      this.focusedSkin = null;
    },

    openViewerFromFocus() {
      this.viewerReturnSkin = this.focusedSkin;
      this.viewerSkin = this.focusedSkin;
      this.focusedSkin = null;
    },

    openAssign(skin) {
      this.assignSkin = skin;
      this.assignSelection = null;
    },

    closeAssign() {
      this.assignSkin = null;
      this.assignSelection = null;
      this.assignSuccess = false;
      this.conflictDialog = null;
      if (this.$refs.assignCategorySelector) {
        this.$refs.assignCategorySelector.reset();
      }
    },

    onAssignSelectionChanged(selectionData) {
      this.assignSelection = selectionData;
    },

    confirmAssign() {
      return this.doAssign(null);
    },

    async resolveConflict(mode) {
      this.conflictDialog = null;
      await this.doAssign(mode);
    },

    async doAssign(mode) {
      // Extract the profile/terrain/token ID to use for the assignment
      const skinType = this.assignSelection.skinType || 'model';
      let profileId;
      if (skinType === 'terrain') {
        profileId = this.assignSelection.selectedTerrain && this.assignSelection.selectedTerrain.Id;
      } else if (skinType === 'token') {
        profileId = this.assignSelection.selectedToken && this.assignSelection.selectedToken.Id;
      } else {
        profileId = this.assignSelection.selectedUnitId;
      }

      const skinId = this.assignSkin.id;
      const gameId = this.assignSelection.game && this.assignSelection.game.IdGame;
      const selectedOptions = this.assignSelection.selectedOptions || [];
      const modifiers = selectedOptions.length > 0 ? selectedOptions : null;

      if (!profileId || !skinId || !gameId) return;

      const body = { skinId, profileId, selectedOptions: modifiers, gameId };
      if (mode) body.mode = mode;

      console.log('[AssignCatalogueSkin] profileId:', profileId, '| idSkin:', skinId, '| modifiers:', modifiers);

      try {
        const res = await fetch('/AssignCatalogueSkin', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.error === 6) {
          this.conflictDialog = { unitName: this.assignSelection.selectedUnitName, skins: data.existingSkins || [] };
          return;
        }
        if (data.error !== 0) {
          alert('Failed to assign skin: ' + (data.result || 'Unknown error'));
          return;
        }
        this.assignSuccess = true;
      } catch (err) {
        alert('Could not reach the server.');
      }
    },

    openSkin(skin) {
      this.viewerSkin = skin;
    },

    closeViewer() {
      this.viewerSkin = null;
      if (this.viewerReturnSkin) {
        this.focusedSkin = this.viewerReturnSkin;
        this.viewerReturnSkin = null;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.catalogue-content {
  max-width: 1100px;
  margin: 0 auto;
}

.catalogue-title {
  margin: 36px 0 20px 0;
  text-align: center;
  font-size: 3rem;
}

/* View switch */
.view-switch {
  display: flex;
  margin-bottom: 14px;
  border: 1px solid #ccc;
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

.switch-btn {
  padding: 7px 24px;
  border: none;
  border-radius: 0;
  background: transparent;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  color: #555;
  transition: background 0.15s, color 0.15s;
}

.switch-btn:not(:last-child) {
  border-right: 1px solid #ccc;
}

.switch-btn.active {
  background: #2980b9;
  color: #fff;
}

/* Filters */
.filter-panel {
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  justify-content: center;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 130px;
}

.filter-group label {
  font-size: 12px;
  font-weight: bold;
  color: #ffffff;
}

.filter-group input,
.filter-group select {
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  height: 32px;
  box-sizing: border-box;
  margin: 0;
}

.filter-clear-group,
.filter-toggle-group {
  min-width: unset;
}

.clear-btn {
  font-size: 12px;
  padding: 0 10px;
  height: 32px;
  box-sizing: border-box;
}

.toggle-switch {
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 32px;
}

.toggle-track {
  position: relative;
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: #ccc;
  transition: background 0.2s;
}

.toggle-track.toggle-on {
  background: #2980b9;
}

.toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-track.toggle-on .toggle-thumb {
  left: 21px;
}

/* Status bar */
.status-bar {
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
  min-height: 20px;
  text-align: center;
}

.status-error {
  color: #c0392b;
}

/* Skin grid is handled by SkinGrid.vue component */

.tag {
  font-size: 10px;
  background: #e8f4fd;
  color: #2980b9;
  padding: 1px 6px;
  border-radius: 10px;
  border: 1px solid #b3d9f0;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.tag:hover {
  background: #2980b9;
  color: #fff;
}

/* Tag filter panel */


/* No results */
.assign-btn {
  margin-top: 6px;
  width: 100%;
  padding: 4px 0;
  font-size: 12px;
  font-weight: bold;
  background: #2980b9;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
}

.assign-btn:hover {
  background: #1f6391;
}

.pick-skin-btn {
  margin-top: 6px;
  width: 100%;
  padding: 4px 0;
  font-size: 12px;
  font-weight: bold;
  background: #27ae60;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
}

.pick-skin-btn:hover {
  background: #219a52;
}

.focus-modal-actions .pick-skin-btn {
  flex: none;
  margin-top: 0;
  width: 120px;
  font-size: 13px;
}

/* Visibility toggle styles live in SkinGrid.vue */
.visibility-switch--overlay .vis-label--on {
  color: #7eb8e0;
}

/* Focus modal */
.focus-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.focus-modal {
  position: relative;
  background: #1a2332;
  border-radius: 12px;
  width: min(560px, 96vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0,0,0,0.6);
  overflow: hidden;
}

.focus-modal-close {
  position: absolute;
  top: 10px;
  right: 12px;
  background: rgba(0,0,0,0.45);
  border: none;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  padding: 2px 7px;
  border-radius: 50%;
  z-index: 1;
}

.focus-modal-close:hover {
  background: rgba(0,0,0,0.75);
}

.focus-modal-image-wrap {
  width: 100%;
  max-height: 512px;
  background: #111;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.focus-modal-img {
  width: 512px;
  height: 512px;
  max-width: 100%;
  max-height: 512px;
  object-fit: contain;
}

.focus-modal-info {
  padding: 14px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.focus-modal-name {
  font-size: 16px;
  font-weight: bold;
  color: #fff;
}

.focus-modal-detail {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #aaa;
}

.focus-modal-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.focus-modal-actions .assign-btn {
  flex: none;
  margin-top: 0;
  width: 100px;
  font-size: 14px;
}

.see3d-btn {
  flex: none;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: bold;
  background: #243044;
  color: #7eb8e0;
  border: 1px solid #3a5070;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.see3d-btn:hover {
  background: #2980b9;
  color: #fff;
  border-color: #2980b9;
}

.report-btn {
  flex: none;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: bold;
  background: transparent;
  color: #e07070;
  border: 1px solid #7a3030;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  margin-left: auto;
}

.report-btn:hover {
  background: #7a2020;
  color: #fff;
  border-color: #a03030;
}

/* Report modal */
.report-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.report-modal {
  background: #1a2332;
  border-radius: 12px;
  width: min(480px, 95vw);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0,0,0,0.6);
  overflow: hidden;
}

.report-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #3a1a1a;
  color: #fff;
  font-size: 15px;
  border-bottom: 1px solid #5a2a2a;
}

.report-modal-close {
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.report-modal-close:hover {
  color: #fff;
}

.report-modal-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.report-intro {
  color: #ccc;
  font-size: 14px;
  margin: 0;
}

.report-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.report-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #2e3d52;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.report-option:hover {
  border-color: #e07070;
  background: rgba(224, 112, 112, 0.06);
}

.report-option input[type="radio"] {
  margin-top: 3px;
  accent-color: #e07070;
  flex-shrink: 0;
}

.report-option-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.report-option-title {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
}

.report-option-desc {
  font-size: 12px;
  color: #888;
}

.report-modal-footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 4px;
}

.report-cancel-btn {
  padding: 8px 18px;
  background: transparent;
  border: 1px solid #3a4a5a;
  color: #aaa;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  transition: border-color 0.15s, color 0.15s;
}

.report-cancel-btn:hover {
  border-color: #7a8a9a;
  color: #fff;
}

.report-submit-btn {
  padding: 8px 22px;
  background: #7a2020;
  border: 1px solid #a03030;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  font-weight: bold;
  transition: background 0.15s;
}

.report-submit-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.report-submit-btn:not(:disabled):hover {
  background: #a03030;
}

.report-done {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
  color: #ccc;
  font-size: 14px;
  text-align: center;
}

/* Mass Report banner */
.mass-report-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #3a1a1a;
  border: 1px solid #7a3030;
  border-radius: 6px;
  padding: 10px 16px;
  margin-bottom: 16px;
  color: #e07070;
  font-size: 13px;
}

.mass-report-start-btn {
  padding: 7px 20px;
  background: #7a2020;
  border: 1px solid #a03030;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  font-weight: bold;
  transition: background 0.15s;
}

.mass-report-start-btn:hover {
  background: #a03030;
}

/* Mass Report floating overlay */
.mass-report-overlay {
  position: fixed;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  pointer-events: none;
}

.mass-report-controls {
  pointer-events: all;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #1a2332;
  border: 1px solid #7a3030;
  border-radius: 10px;
  padding: 14px 22px;
  box-shadow: 0 6px 32px rgba(0,0,0,0.7);
  white-space: nowrap;
}

.mass-report-count {
  font-size: 14px;
  color: #ccc;
}

.mass-report-cancel-btn {
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #3a4a5a;
  color: #aaa;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  transition: border-color 0.15s, color 0.15s;
}

.mass-report-cancel-btn:hover {
  border-color: #7a8a9a;
  color: #fff;
}

.mass-report-finish-btn {
  padding: 9px 26px;
  background: #7a2020;
  border: 1px solid #a03030;
  color: #fff;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: background 0.15s;
}

.mass-report-finish-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mass-report-finish-btn:not(:disabled):hover {
  background: #a03030;
}

.mass-report-done-msg {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #ccc;
  font-size: 14px;
}

/* Viewer modal */
.viewer-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.viewer-modal {
  position: relative;
  background: #111;
  border-radius: 12px;
  width: min(860px, 96vw);
  height: min(680px, 90vh);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0,0,0,0.6);
  overflow: hidden;
}

.viewer-modal-close {
  position: absolute;
  top: 10px;
  right: 12px;
  background: rgba(0,0,0,0.55);
  border: none;
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  padding: 2px 7px;
  border-radius: 50%;
  z-index: 10;
}

.viewer-modal-close:hover {
  background: rgba(0,0,0,0.85);
}

.viewer-modal-body {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.viewer-modal-body .model-viewer {
  width: 100%;
  height: 100%;
}

.viewer-no-model {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
  color: #aaa;
}

.viewer-fallback-img {
  max-width: 320px;
  max-height: 320px;
  object-fit: contain;
  border-radius: 8px;
}

/* Assign modal */
.assign-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.assign-modal {
  background: #1a2332;
  border-radius: 12px;
  width: min(720px, 95vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0,0,0,0.5);
  overflow: hidden;
  position: relative;
}

.assign-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #243044;
  color: #fff;
  font-size: 15px;
  border-bottom: 1px solid #334;
}

.assign-modal-close {
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 18px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.assign-modal-close:hover {
  color: #fff;
}

.assign-modal-body {
  overflow-y: auto;
  flex: 1;
}

.assign-modal-footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 14px 20px;
  background: #243044;
  border-top: 1px solid #334;
}

.assign-confirm-btn {
  background: #27ae60;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 7px 20px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.15s;
}

.assign-confirm-btn:disabled {
  background: #555;
  cursor: not-allowed;
}

.assign-confirm-btn:not(:disabled):hover {
  background: #1e8449;
}

.assign-cancel-btn {
  background: #c0392b;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 7px 20px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.15s;
}

.assign-cancel-btn:hover {
  background: #922b21;
}

/* Conflict dialog overlay */
.conflict-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 18, 30, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  z-index: 10;
}

.conflict-dialog {
  background: #1a2535;
  border: 1px solid #2c3e50;
  border-radius: 10px;
  padding: 28px 24px 20px;
  max-width: 380px;
  width: 90%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.conflict-title {
  color: #e8eef5;
  font-size: 0.95em;
  line-height: 1.5;
  margin: 0;
}

.conflict-hint {
  color: #7a8fa6;
  font-size: 0.82em;
  line-height: 1.5;
  margin: 0;
}

.conflict-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.conflict-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #2c3e50;
  background: #0d1520;
}

.conflict-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}

.conflict-btn {
  border: none;
  border-radius: 6px;
  padding: 7px 18px;
  font-weight: bold;
  font-size: 0.88em;
  cursor: pointer;
  transition: background 0.15s;
}

.conflict-btn--cancel {
  background: #c0392b;
  color: #fff;
}

.conflict-btn--cancel:hover {
  background: #a93226;
}

.conflict-btn--override {
  background: #2471a3;
  color: #fff;
}

.conflict-btn--override:hover {
  background: #1a5276;
}

.conflict-btn--add {
  background: #2471a3;
  color: #fff;
}

.conflict-btn--add:hover {
  background: #1a5276;
}

/* Auth prompt */
.auth-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 64px 24px;
  gap: 16px;
}

.auth-prompt-icon {
  font-size: 3rem;
}

.auth-prompt-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #e8eef5;
  margin: 0;
}

.auth-prompt-body {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #7a9ab8;
  max-width: 380px;
  margin: 0;
}

.auth-prompt-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.auth-prompt-btn {
  display: inline-block;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.92rem;
  text-decoration: none;
  transition: background 0.15s, transform 0.1s;
}

.auth-prompt-btn--primary {
  background: #2471a3;
  color: #fff;
  border: 2px solid #2471a3;
}

.auth-prompt-btn--primary:hover {
  background: #1a5276;
  border-color: #1a5276;
  transform: translateY(-1px);
}

.auth-prompt-btn--ghost {
  background: transparent;
  color: #5dade2;
  border: 2px solid #2471a3;
}

.auth-prompt-btn--ghost:hover {
  background: rgba(36, 113, 163, 0.15);
  transform: translateY(-1px);
}

/* No results */
.no-results {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 14px;
}

/* Pagination */
.pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid #eee;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.page-btn {
  min-width: 34px;
}

.page-btn-active {
  background: #1565c0;
  color: #fff;
  border-color: #1565c0;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(21, 101, 192, 0.45);
}

.page-ellipsis {
  padding: 0 6px;
  color: #999;
  user-select: none;
  line-height: 1;
}

.per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.per-page-selector select {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
</style>
